import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  work_email: "",
  company: "",
  website: "",
  company_size: "",
  message: "",
};

export default function CTASection() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.work_email || !form.company) {
      toast.error("Name, work email and company are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Demo request received. We'll reach out within 24h.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail[0]?.msg
          : null) ||
        "Could not send. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="cta-section"
      className="relative py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF4500]/10 blur-[140px] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="mb-14 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-4">
            Book a demo
          </p>
          <h2 className="font-editorial font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            Show up to <span className="italic">close.</span>
            <br />
            Let us handle the <span className="italic">rest.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Form */}
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="lead-form"
            className="lg:col-span-7 rounded-3xl border border-[#2A2E3D] bg-[#151821]/80 backdrop-blur p-8 md:p-10"
          >
            {submitted ? (
              <div
                data-testid="form-success"
                className="flex flex-col items-start gap-4 py-6"
              >
                <CheckCircle2 className="w-10 h-10 text-[#A7F3D0]" />
                <h3 className="font-editorial text-3xl text-[#EDEDED]">
                  We&rsquo;ve got your details.
                </h3>
                <p className="text-[#9CA3AF] max-w-md">
                  A growth specialist will reach out within 24 hours with a
                  custom ICP brief and proposed meeting volume.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="form-reset-button"
                  className="mt-4 text-sm uppercase tracking-[0.25em] text-[#FF4500] hover:text-[#E03C00] transition-colors"
                >
                  Send another →
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Full name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Jane Doe"
                  testId="form-input-name"
                  required
                />
                <Field
                  label="Work email"
                  name="work_email"
                  type="email"
                  value={form.work_email}
                  onChange={onChange}
                  placeholder="jane@acme.com"
                  testId="form-input-email"
                  required
                />
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Acme Inc."
                  testId="form-input-company"
                  required
                />
                <Field
                  label="Website"
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  placeholder="acme.com"
                  testId="form-input-website"
                />
                <div className="sm:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.3em] text-[#9CA3AF] mb-2 block">
                    Company size
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["1-20", "21-100", "101-500", "500+"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`form-size-${s}`}
                        onClick={() =>
                          setForm((f) => ({ ...f, company_size: s }))
                        }
                        className={`px-4 py-2 rounded-full text-sm border transition-all ${
                          form.company_size === s
                            ? "bg-[#FF4500] border-[#FF4500] text-white"
                            : "bg-transparent border-[#2A2E3D] text-[#9CA3AF] hover:border-[#EDEDED] hover:text-[#EDEDED]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#FF4500] hover:bg-[#E03C00] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,69,0,0.4)]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Request my demo <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="mt-4 text-xs text-[#4B5563]">
                    By submitting you agree to be contacted by Prospectivly.
                    We never sell your data.
                  </p>
                </div>
              </div>
            )}
          </motion.form>

          {/* Calendar placeholder card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="calendar-embed"
            className="lg:col-span-5 rounded-3xl border border-[#2A2E3D] bg-gradient-to-br from-[#151821] to-[#0F1117] p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-center gap-3 mb-4">
              <CalendarDays className="w-5 h-5 text-[#FF4500]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#9CA3AF]">
                Or pick a slot instantly
              </span>
            </div>
            <h3 className="font-editorial text-3xl text-[#EDEDED] tracking-tight mb-2">
              30-minute strategy call.
            </h3>
            <p className="text-[#9CA3AF] mb-6">
              We&rsquo;ll diagnose your funnel, share benchmarks from BFSI &amp; SaaS,
              and outline a 90-day pipeline plan.
            </p>

            {/* Mock calendar */}
            <div className="rounded-2xl border border-[#2A2E3D] bg-[#0B0D12]/60 p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <span className="font-editorial text-lg text-[#EDEDED]">
                  This week
                </span>
                <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">
                  IST / GMT+5:30
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { d: "Mon", t: "10:00" },
                  { d: "Tue", t: "14:30" },
                  { d: "Wed", t: "11:00" },
                  { d: "Thu", t: "16:00" },
                  { d: "Fri", t: "09:30" },
                  { d: "Fri", t: "15:00" },
                  { d: "Sat", t: "12:00" },
                  { d: "Mon+", t: "More →" },
                ].map((slot, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`slot-${i}`}
                    onClick={() =>
                      toast.message(
                        `${slot.d} ${slot.t}: submit the form to confirm.`
                      )
                    }
                    className="rounded-xl border border-[#2A2E3D] hover:border-[#FF4500] hover:bg-[#FF4500]/10 transition-all py-3 text-center"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">
                      {slot.d}
                    </div>
                    <div className="text-sm text-[#EDEDED] mt-1">{slot.t}</div>
                  </button>
                ))}
              </div>
              <p className="mt-4 text-[10px] uppercase tracking-widest text-[#4B5563]">
                * Live calendar embed connects post-signup
              </p>
            </div>

            <div className="mt-auto flex items-center gap-3 text-sm text-[#9CA3AF]">
              <div className="flex -space-x-2">
                <span className="w-7 h-7 rounded-full bg-[#FF4500] border-2 border-[#0B0D12]" />
                <span className="w-7 h-7 rounded-full bg-[#A7F3D0] border-2 border-[#0B0D12]" />
                <span className="w-7 h-7 rounded-full bg-[#FFDAB9] border-2 border-[#0B0D12]" />
              </div>
              <span>Growth team replies within 24h.</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, placeholder, testId, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-[#9CA3AF] mb-2 block">
        {label} {required && <span className="text-[#FF4500]">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        required={required}
        className="w-full bg-[#0F1117] border border-[#2A2E3D] focus:border-[#FF4500] focus:ring-1 focus:ring-[#FF4500]/40 outline-none rounded-2xl px-4 py-3 text-[#EDEDED] placeholder:text-[#4B5563] transition-colors"
      />
    </div>
  );
}
